import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Input,
  Select,
  Card,
  DataTable,
  Modal,
} from "../../components/ui";
import { ConfirmModal } from "../../components/ui/Modal";

const FREQUENZA_UNITA = [
  { value: "100000000", label: "Giorni" },
  { value: "100000001", label: "Settimane" },
  { value: "100000002", label: "Mesi" },
  { value: "100000003", label: "Anni" },
];

const getFrequenzaLabel = (value) => {
  const found = FREQUENZA_UNITA.find((f) => f.value === String(value));
  return found ? found.label : "-";
};

const EMPTY_FORM = {
  phc_name: "",
  phc_descrizione: "",
  phc_macrocategoria: "",
  phc_riferimentonormativo: "",
  phc_frequenzavalore: "",
  phc_frequenzaunita: "",
  phc_tipfornitura_id: "",
  phc_attiva: true,
  phc_notificarspp: false,
  phc_obbligatorio: false,
};

function TipologieInterventoPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [tipologie, setTipologie] = useState([]);
  const [tipologieFornitura, setTipologieFornitura] = useState([]);
  const [macrocategorie, setMacrocategorie] = useState([]);
  const [rsaList, setRsaList] = useState([]);

  const getMacrocategoriaLabel = (value) => {
    const found = macrocategorie.find((m) => m.value === String(value));
    return found ? found.label : "-";
  };

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState(EMPTY_FORM);

  // RSA obblighi state: Set di rsaId selezionati + Map rsaId→obbligoId esistenti
  const [rsaSelezionate, setRsaSelezionate] = useState(new Set());
  const [obblighiEsistenti, setObblighiEsistenti] = useState(new Map()); // rsaId → obbligoId

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tipologieData, fornitureData, macrocategorieData, rsaData] =
        await Promise.all([
          apiRequest("/tipologie-intervento"),
          apiRequest("/tipologie-fornitura"),
          apiRequest("/tipologie-intervento/macrocategorie"),
          apiRequest("/rsa"),
        ]);
      setTipologie(tipologieData.value || []);
      setTipologieFornitura(fornitureData.value || []);
      setMacrocategorie(macrocategorieData || []);
      setRsaList(rsaData.value || []);
    } catch (error) {
      console.error("Error loading data:", error);
      showToast("Errore nel caricamento dei dati", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setRsaSelezionate(new Set());
    setObblighiEsistenti(new Map());
    setModalOpen(true);
  };

  const handleOpenEdit = async (item) => {
    setEditingItem(item);
    setFormData({
      phc_name: item.nome || item.phc_name || "",
      phc_descrizione: item.descrizione || item.phc_descrizione || "",
      phc_macrocategoria: String(
        item.macrocategoria || item.phc_macrocategoria || "",
      ),
      phc_riferimentonormativo:
        item.riferimentoNormativo || item.phc_riferimentonormativo || "",
      phc_frequenzavalore:
        item.frequenzaValore || item.phc_frequenzavalore || "",
      phc_frequenzaunita: String(
        item.frequenzaUnita || item.phc_frequenzaunita || "",
      ),
      phc_tipfornitura_id:
        item.tipologiaFornituraId || item.phc_tipfornitura_id || "",
      phc_attiva:
        item.attiva !== undefined
          ? item.attiva
          : item.phc_attiva !== undefined
            ? item.phc_attiva
            : true,
      phc_notificarspp: item.notificaRspp || item.phc_notificarspp || false,
      phc_obbligatorio: item.obbligatorio || item.phc_obbligatorio || false,
    });

    // Carica obblighi RSA esistenti per questa tipologia
    const selezionate = new Set();
    const esistenti = new Map();
    if (item.obbligatorio || item.phc_obbligatorio) {
      try {
        const obbligoData = await apiRequest(
          `/obblighi-tipologia?tipologiaId=${item.id}&soloAttivi=false`,
        );
        for (const o of obbligoData.value || []) {
          selezionate.add(o.rsaId);
          esistenti.set(o.rsaId, o.id);
        }
      } catch {
        // ignora errore caricamento obblighi, non blocca l'apertura
      }
    }
    setRsaSelezionate(selezionate);
    setObblighiEsistenti(esistenti);
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleRsa = (rsaId) => {
    setRsaSelezionate((prev) => {
      const next = new Set(prev);
      if (next.has(rsaId)) next.delete(rsaId);
      else next.add(rsaId);
      return next;
    });
  };

  // Sincronizza le RSA obbligatorie dopo aver salvato la tipologia
  const syncObblighi = async (tipologiaId) => {
    if (!formData.phc_obbligatorio) {
      // Se il flag è stato disattivato, elimina tutti gli obblighi esistenti
      for (const [, obbligoId] of obblighiEsistenti) {
        try {
          await apiRequest(`/obblighi-tipologia/${obbligoId}`, {
            method: "DELETE",
          });
        } catch {
          // ignora errori singoli
        }
      }
      return;
    }

    const rsaDaAggiungere = [...rsaSelezionate].filter(
      (id) => !obblighiEsistenti.has(id),
    );
    const rsaDaRimuovere = [...obblighiEsistenti.keys()].filter(
      (id) => !rsaSelezionate.has(id),
    );

    await Promise.allSettled([
      ...rsaDaAggiungere.map((rsaId) =>
        apiRequest("/obblighi-tipologia", {
          method: "POST",
          body: JSON.stringify({ tipologiaId, rsaId }),
        }),
      ),
      ...rsaDaRimuovere.map((rsaId) =>
        apiRequest(`/obblighi-tipologia/${obblighiEsistenti.get(rsaId)}`, {
          method: "DELETE",
        }),
      ),
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phc_name.trim()) {
      showToast("Il nome è obbligatorio", "error");
      return;
    }

    if (formData.phc_obbligatorio && rsaSelezionate.size === 0) {
      showToast(
        "Seleziona almeno una RSA per gli interventi obbligatori",
        "error",
      );
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        phc_macrocategoria: formData.phc_macrocategoria
          ? parseInt(formData.phc_macrocategoria)
          : null,
        phc_frequenzavalore: formData.phc_frequenzavalore
          ? parseInt(formData.phc_frequenzavalore)
          : null,
        phc_frequenzaunita: formData.phc_frequenzaunita
          ? parseInt(formData.phc_frequenzaunita)
          : null,
      };

      let tipologiaId;
      if (editingItem) {
        await apiRequest(`/tipologie-intervento/${editingItem.id}`, {
          method: "PUT",
          body: JSON.stringify(dataToSend),
        });
        tipologiaId = editingItem.id;
      } else {
        const result = await apiRequest("/tipologie-intervento", {
          method: "POST",
          body: JSON.stringify(dataToSend),
        });
        tipologiaId = result.id;
      }

      // Sincronizza obblighi RSA
      await syncObblighi(tipologiaId);

      showToast(
        editingItem
          ? "Tipologia aggiornata con successo"
          : "Tipologia creata con successo",
        "success",
      );
      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving tipologia:", error);
      showToast(error.message || "Errore durante il salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await apiRequest(`/tipologie-intervento/${itemToDelete.id}`, {
        method: "DELETE",
      });
      showToast("Tipologia eliminata con successo", "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting tipologia:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const getTipologiaFornituraName = (id) => {
    const found = tipologieFornitura.find((t) => t.id === id);
    return found ? found.nome || found.phc_name : "-";
  };

  const handleExportExcel = () => {
    const rows = tipologie.map((t) => {
      const valore = t.frequenzaValore || t.phc_frequenzavalore;
      const unita = t.frequenzaUnita || t.phc_frequenzaunita;
      const frequenza =
        valore && unita
          ? `Ogni ${valore} ${getFrequenzaLabel(unita).toLowerCase()}`
          : "";

      return {
        Tipologia: t.nome || t.phc_name || "",
        Descrizione: t.descrizione || t.phc_descrizione || "",
        Macrocategoria: getMacrocategoriaLabel(
          t.macrocategoria || t.phc_macrocategoria,
        ),
        "Tipologia Fornitura": getTipologiaFornituraName(
          t.tipologiaFornituraId || t.phc_tipfornitura_id,
        ),
        Frequenza: frequenza,
        "Rif. Normativo":
          t.riferimentoNormativo || t.phc_riferimentonormativo || "",
        Obbligatorio: t.obbligatorio || t.phc_obbligatorio ? "Sì" : "No",
        "Alert RSPP": t.notificaRspp || t.phc_notificarspp ? "Sì" : "No",
        Stato:
          (t.attiva !== undefined ? t.attiva : t.phc_attiva) !== false
            ? "Attiva"
            : "Inattiva",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 40 },
      { wch: 50 },
      { wch: 25 },
      { wch: 25 },
      { wch: 20 },
      { wch: 35 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tipologie Intervento");
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `tipologie_intervento_${date}.xlsx`);
  };

  const columns = [
    {
      key: "nome",
      label: "Nome",
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">
            {row.nome || row.phc_name}
          </div>
          {(row.descrizione || row.phc_descrizione) && (
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {row.descrizione || row.phc_descrizione}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "macrocategoria",
      label: "Macrocategoria",
      sortable: true,
      render: (row) =>
        getMacrocategoriaLabel(row.macrocategoria || row.phc_macrocategoria),
    },
    {
      key: "frequenza",
      label: "Frequenza",
      render: (row) => {
        const valore = row.frequenzaValore || row.phc_frequenzavalore;
        const unita = row.frequenzaUnita || row.phc_frequenzaunita;
        if (valore && unita) {
          return `Ogni ${valore} ${getFrequenzaLabel(unita).toLowerCase()}`;
        }
        return "-";
      },
    },
    {
      key: "tipologiaFornitura",
      label: "Tipologia Fornitura",
      render: (row) =>
        getTipologiaFornituraName(
          row.tipologiaFornituraId || row.phc_tipfornitura_id,
        ),
    },
    {
      key: "riferimentoNormativo",
      label: "Rif. Normativo",
      render: (row) => {
        const rif = row.riferimentoNormativo || row.phc_riferimentonormativo;
        return rif ? (
          <span
            className="text-xs text-gray-600 truncate max-w-[150px] block"
            title={rif}
          >
            {rif}
          </span>
        ) : (
          "-"
        );
      },
    },
    {
      key: "obbligatorio",
      label: "Obbligatorio",
      render: (row) => {
        const obbl = row.obbligatorio || row.phc_obbligatorio;
        return obbl ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
            Sì
          </span>
        ) : (
          <span className="text-gray-400 text-xs">-</span>
        );
      },
    },
    {
      key: "notificaRspp",
      label: "Alert RSPP",
      render: (row) => {
        const notify = row.notificaRspp || row.phc_notificarspp;
        return notify ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
            Attivo
          </span>
        ) : (
          <span className="text-gray-400 text-xs">-</span>
        );
      },
    },
    {
      key: "attiva",
      label: "Stato",
      render: (row) => {
        const isActive = row.attiva !== undefined ? row.attiva : row.phc_attiva;
        return (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              isActive
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {isActive ? "Attiva" : "Inattiva"}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Azioni",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEdit(row);
            }}
            className="p-1 text-primary-600 hover:text-primary-800"
            title="Modifica"
          >
            ✏️
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              confirmDelete(row);
            }}
            className="p-1 text-red-600 hover:text-red-800"
            title="Elimina"
          >
            🗑️
          </button>
        </div>
      ),
    },
  ];

  const tipologieFornituraOptions = tipologieFornitura.map((t) => ({
    value: t.id,
    label: t.nome || t.phc_name,
  }));

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <a href="/admin" className="hover:text-primary-600">
              Admin
            </a>
            <span>/</span>
            <span>Tipologie Intervento</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Tipologie Intervento
          </h1>
          <p className="text-gray-500 mt-1">
            Gestisci le tipologie di intervento manutentivo e le relative
            frequenze
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={handleExportExcel}
            disabled={tipologie.length === 0}
            title="Esporta in Excel"
          >
            ⬇ Esporta Excel
          </Button>
          <Button variant="primary" onClick={handleOpenCreate}>
            + Nuova Tipologia
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-2xl font-bold text-gray-900">
            {tipologie.length}
          </div>
          <div className="text-sm text-gray-500">Totale Tipologie</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600">
            {
              tipologie.filter(
                (t) => t.attiva !== false && t.phc_attiva !== false,
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Attive</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-primary-600">
            {
              tipologie.filter(
                (t) => t.frequenzaValore || t.phc_frequenzavalore,
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Con Frequenza</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-purple-600">
            {
              tipologie.filter(
                (t) => t.riferimentoNormativo || t.phc_riferimentonormativo,
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Con Normativa</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-amber-600">
            {
              tipologie.filter((t) => t.notificaRspp || t.phc_notificarspp)
                .length
            }
          </div>
          <div className="text-sm text-gray-500">Con Alert RSPP</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-red-600">
            {
              tipologie.filter((t) => t.obbligatorio || t.phc_obbligatorio)
                .length
            }
          </div>
          <div className="text-sm text-gray-500">Obbligatorie</div>
        </Card>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <DataTable
          data={tipologie}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca tipologia intervento..."
          emptyMessage="Nessuna tipologia intervento trovata"
          onRowClick={handleOpenEdit}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          editingItem
            ? "Modifica Tipologia Intervento"
            : "Nuova Tipologia Intervento"
        }
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome *"
            name="phc_name"
            value={formData.phc_name}
            onChange={handleChange}
            placeholder="es. Verifica Quadri Elettrici"
            maxLength={200}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrizione
            </label>
            <textarea
              name="phc_descrizione"
              value={formData.phc_descrizione}
              onChange={handleChange}
              placeholder="Descrizione dell'intervento..."
              className="form-input w-full h-24"
              maxLength={2000}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Macrocategoria"
              name="phc_macrocategoria"
              value={formData.phc_macrocategoria}
              onChange={handleChange}
              options={macrocategorie}
              placeholder="Seleziona categoria..."
            />
            <Select
              label="Tipologia Fornitura"
              name="phc_tipfornitura_id"
              value={formData.phc_tipfornitura_id}
              onChange={handleChange}
              options={tipologieFornituraOptions}
              placeholder="Seleziona tipologia..."
            />
          </div>

          {/* Frequenza */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Frequenza Programmata
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Valore"
                name="phc_frequenzavalore"
                type="number"
                min="1"
                value={formData.phc_frequenzavalore}
                onChange={handleChange}
                placeholder="es. 6"
              />
              <Select
                label="Unità"
                name="phc_frequenzaunita"
                value={formData.phc_frequenzaunita}
                onChange={handleChange}
                options={FREQUENZA_UNITA}
                placeholder="Seleziona..."
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Es: ogni 6 mesi, ogni 1 anno, ogni 30 giorni
            </p>
          </div>

          <Input
            label="Riferimento Normativo"
            name="phc_riferimentonormativo"
            value={formData.phc_riferimentonormativo}
            onChange={handleChange}
            placeholder="es. DPR 462/01, D.Lgs 81/08 art. 71"
            helperText="Normativa di riferimento per l'intervento"
            maxLength={500}
          />

          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="phc_attiva"
                checked={formData.phc_attiva}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <span className="text-gray-700">Tipologia attiva</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="phc_obbligatorio"
                checked={formData.phc_obbligatorio}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-red-600"
              />
              <div>
                <span className="text-gray-700">
                  Intervento obbligatorio per legge
                </span>
                <p className="text-xs text-gray-500">
                  Segnala come gap normativo se manca una scadenza attiva nelle
                  RSA selezionate
                </p>
              </div>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="phc_notificarspp"
                checked={formData.phc_notificarspp}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-amber-600"
              />
              <div>
                <span className="text-gray-700">
                  Notifica RSPP alla creazione intervento
                </span>
                <p className="text-xs text-gray-500">
                  Invia email automatica agli RSPP quando viene registrato un
                  intervento di questa tipologia
                </p>
              </div>
            </label>
          </div>

          {/* Sezione RSA obbligatorie — visibile solo quando il flag è attivo */}
          {formData.phc_obbligatorio && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-sm font-semibold text-red-800 mb-1">
                RSA dove l&apos;intervento è obbligatorio
              </h3>
              <p className="text-xs text-red-600 mb-3">
                Seleziona le strutture per cui questo intervento deve essere
                coperto da una scadenza attiva.
              </p>
              {rsaList.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Nessuna RSA disponibile.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {rsaList.map((rsa) => {
                    const rsaId = rsa.id || rsa.phc_rsaid;
                    const nome = rsa.nome || rsa.phc_name;
                    return (
                      <label
                        key={rsaId}
                        className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-red-100"
                      >
                        <input
                          type="checkbox"
                          checked={rsaSelezionate.has(rsaId)}
                          onChange={() => toggleRsa(rsaId)}
                          className="h-4 w-4 rounded border-red-300 text-red-600"
                        />
                        <span className="text-sm text-gray-800">{nome}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {rsaList.length > 0 && (
                <p className="text-xs text-red-700 mt-2">
                  {rsaSelezionate.size} di {rsaList.length} RSA selezionate
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setModalOpen(false)}
            >
              Annulla
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Salvataggio..." : editingItem ? "Aggiorna" : "Crea"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Tipologia Intervento"
        message={`Sei sicuro di voler eliminare la tipologia "${itemToDelete?.nome || itemToDelete?.phc_name}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default TipologieInterventoPage;
